/**
 * Captcha Validation API Service
 * Handles captcha token validation before proceeding with authenticated actions
 */

import { apiClient } from './client';
import { getConfig } from '../config';

export interface CaptchaValidationRequest {
  token: string;
}

export interface CaptchaValidationResponse {
  success: boolean;
  message: string;
}

/**
 * Validate captcha token
 */
export const validateCaptcha = async (
  request: CaptchaValidationRequest
): Promise<CaptchaValidationResponse> => {
  console.log('🔐 [CaptchaService] ====== CAPTCHA VALIDATION START ======');
  console.log('🔐 [CaptchaService] Request details:', {
    tokenLength: request.token.length,
    tokenPreview: request.token.substring(0, 20) + '...',
    timestamp: new Date().toISOString()
  });

  // Get current config for logging
  const config = getConfig();

  // 🚨 COMPLETE API PAYLOAD LOGGING
  console.log('📋 [CaptchaService] COMPLETE API PAYLOAD for /api/captcha/validation:');
  console.log('📋 [CaptchaService] URL:', '/api/captcha/validation');
  console.log('📋 [CaptchaService] Method:', 'POST');
  console.log('📋 [CaptchaService] Headers:', {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  });
  console.log('📋 [CaptchaService] Full Request Body (JSON):');
  console.log(JSON.stringify(request, null, 2));

  // Also log as curl command for easy testing - use actual API base URL
  console.log('📋 [CaptchaService] CURL Command Equivalent:');
  const curlCommand = `curl -X POST ${config.apiBaseUrl}/api/captcha/validation \\
  -H "Content-Type: application/json" \\
  -H "Accept: application/json" \\
  -d '${JSON.stringify(request, null, 2)}'`;
  console.log(curlCommand);

  try {
    console.log('📡 [CaptchaService] Making API call to /api/captcha/validation...');
    const response = await apiClient.post<CaptchaValidationResponse>(
      '/api/captcha/validation',
      request
    );

    console.log('📡 [CaptchaService] API Response:', {
      status: response.status,
      statusText: response.statusText,
      data: response.data,
      timestamp: new Date().toISOString()
    });

    console.log('✅ [CaptchaService] ====== CAPTCHA VALIDATION SUCCESS ======');
    return response.data;
  } catch (error: any) {
    console.error('❌ [CaptchaService] ====== CAPTCHA VALIDATION ERROR ======');
    console.error('❌ [CaptchaService] Error details:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      stack: error.stack,
      timestamp: new Date().toISOString()
    });

    // Log the exact request that failed
    console.error('❌ [CaptchaService] Failed request payload:');
    console.error(JSON.stringify(request, null, 2));

    // Extract error message from response
    const errorMessage =
      error.response?.data?.message || 'Captcha validation failed. Please try again.';

    console.error('❌ [CaptchaService] Throwing error:', errorMessage);
    throw new Error(errorMessage);
  }
};
